import React, { useContext, useEffect, useMemo, useState } from "react"
import { StyleSheet, Text, TouchableOpacity, View } from "react-native"
import { Ionicons } from "@expo/vector-icons"
import { CommonActions, Link, StackActions, useNavigation } from "@react-navigation/native"
import { MatchRatingInfo, CustomerFeaturedBusiness, PopulatedValue, Company } from "@ecountabl/lib"

import { useAppSelector } from "./store"
import { ThemeContext } from "./utility/context/ThemeContext"
import { MAX_CONTENT_WIDTH } from "./config"
import { ShadowView, textStyles } from "./style"
import ThemedDialog from "./style/dialog"
import Bullet from "./style/bullet"
import Logo from "./utility/logo"
import { ValueBadge } from "./utility/value-badge"
import Haptics from "./utility/haptics"
import CompanyViewFlatlist from "./utility/companyViewFlatlist"
import Analytics from "./utility/analytics"
import { DashboardBusinessesSkeleton } from "./dashboard-businesses-skeleton"

const DashboardBusinesses:React.FC = () => {
  const { data, error, status: featuredTagsStatus } = useAppSelector(s => s.featuredTags)
  const loading = featuredTagsStatus === 'idle' || featuredTagsStatus === 'loading'
  const { user } = useAppSelector(s => s.auth)
  const valuesMap = useAppSelector(s => s.values.map) as Record<string, PopulatedValue>
  const theme = useContext(ThemeContext)
  const { bgMain, primary, danger, textMain } = theme.theme.colors
  const customerId = user?.customer?._id

  const [primaryList, setPrimaryList] = useState(null)
  const [secondaryList, setSecondaryList] = useState(null)
  const [errorState, setErrorState] = useState(false)

  const navigation = useNavigation()

  // Match valuesAligned items (which are keyed by Value label) to PopulatedValue metadata,
  // so we can render consistent icon/color badges like the alternatives UI.
  const valueByLabel = useMemo(() => {
    const m = new Map<string, PopulatedValue>()
    try {
      Object.values(valuesMap || {}).forEach((v) => {
        if (v?.label) m.set(v.label, v)
      })
    } catch (_) { /* noop */ }
    return m
  }, [valuesMap])

  const userSelectedValueSlugs = useMemo(() => {
    const slugs = new Set<string>()
    try {
      const userMap = user?.valuedImpactAreaMap || {}
      Object.values(valuesMap || {}).forEach((v: any) => {
        const slug = typeof v?.slug === 'string' ? v.slug : undefined
        if (!slug) return
        const ias = Array.isArray(v?.impactAreas) ? v.impactAreas : []
        const anySelected = ias.some((ia: any) => !!userMap?.[ia?._id])
        if (anySelected) slugs.add(slug)
      })
    } catch (_) { /* noop */ }
    return slugs
  }, [user, valuesMap])

  const matchedValues = (co: Partial<CustomerFeaturedBusiness>): PopulatedValue[] => {
    type WithValuesAligned = Partial<CustomerFeaturedBusiness> & { valuesAligned?: unknown }
    const src = co as WithValuesAligned
    const aligned: MatchRatingInfo[] = Array.isArray(src.valuesAligned)
      ? (src.valuesAligned as MatchRatingInfo[])
      : []
    if (!aligned.length) return []

    const out: PopulatedValue[] = []
    const seen = new Set<string>()
    aligned.forEach((a) => {
      const slug = typeof (a as any)?.slug === "string" ? (a as any).slug : ""
      const label = typeof a?.label === "string" ? a.label : ""
      if (!slug && !label) return
      // Only show badges for values the user has selected.
      if (userSelectedValueSlugs.size && slug && !userSelectedValueSlugs.has(slug)) return

      const v = (slug && valuesMap?.[slug]) ? valuesMap[slug] : valueByLabel.get(label)
      const key = v?.slug || slug || label
      if (!v || seen.has(key)) return
      seen.add(key)
      out.push(v)
    })
    return out
  }

  const ValueBadgesRow = ({ company }: { company: Partial<CustomerFeaturedBusiness> }) => {
    const badges = matchedValues(company)
    // Always render a fixed-height row to preserve whitespace even when there are no matches.
    if (!badges.length) return <View style={styles.valueBadgesRow} />

    return (
      <View pointerEvents="none" style={styles.valueBadgesRow}>
        {badges.map((v, idx) => (
          <ValueBadge
            key={v.slug || `${v.label}-${idx}`}
            color={v.color}
            accentFallbackColor={primary}
            icon={v.icon}
            label={v.label}
            size={24}
            iconSize={14}
          />
        ))}
      </View>
    )
  }

  useEffect(() => {
    if (loading || error || !data || data.length === 0) return

    if (!customerId) return

    const lists = data as unknown as Array<{ _id?: string }>
    const primary = lists.find(d => d?._id == `${customerId}-primary`) || null
    const secondary = lists.find(d => d?._id == `${customerId}-secondary`) || null

    setPrimaryList(primary)
    setSecondaryList(secondary)

  }, [loading, error, data, customerId])

  const renderFeaturedBusiness = (
    { item, index, source }: {
      item: Partial<CustomerFeaturedBusiness>,
      index: number,
      source: string
    }
  ) => {
    const featuredDescription = item?.featured_info?.description || item?.description || item?.segment || ''
    const logoCompany: Company = {
      _id: String(item._id || ''),
      name: item.name || '',
      logourl: item.logourl,
      logoref: item.logoref,
      webpurl: item.webpurl,
    }

    return (
      <ShadowView
        style={[
          styles.featuredCard,
          {backgroundColor: bgMain, alignSelf: 'stretch'}
        ]}
      >
        <TouchableOpacity
          onPress={() => {
            Haptics.selection()
            Analytics.track('company_clicked', {
              companyId: item._id,
              companyName: item.name,
              index: index,
              source: source,
            })
            navigation.dispatch(StackActions.push('Company', {
              id: item._id,
              index: index,
              source: source,
              name: item.name,
            }))
          }}
        >
          <View
            style={{
              width: "100%",
              height: undefined,
              aspectRatio: 1,
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8,
              overflow: "hidden"
            }}
          >
            <Logo
              company={logoCompany}
              style={{
                width: 250,
                height: 250,
                borderRadius: 8,
              }}
            />
          </View>
          <View style={[styles.featuredTextContainer]}>
            <ValueBadgesRow company={item} />
            <Text style={[textStyles.h4, {color: textMain}]} selectable={false}>{item.name}</Text>
            <Text style={[textStyles.body, {color: textMain}]} selectable={false} ellipsizeMode="tail">
              {featuredDescription}
            </Text>
          </View> 
        </TouchableOpacity>
      </ShadowView>
    )
  }

  const renderFeaturedBusinessContent = () => {
    if (loading) {
      return <DashboardBusinessesSkeleton cardsPerRow={4} cardWidth={250} />
    }

    if (error) {
      return (
        <ShadowView
          style={[
            styles.errorCard,
            {marginBottom: 5, backgroundColor: bgMain}
          ]}
        >
          <Bullet color={danger} size={6} style={{paddingRight: 6}} />
          <Text style={[textStyles.body,{color: textMain}]}>
            There was an issue gathering featured businesses
          </Text>
        </ShadowView>
      )
    }

    if (!primaryList && !secondaryList) {
      return (
        <View style={{flex: 1, justifyContent: "center", alignItems: "center"}}>
          <Text style={[textStyles.body, {color: textMain}]}>
            Interested in seeing some excellent businesses?{' '}
            Press the button below to explore more companies.
          </Text>
        </View>
      )
    }

    const PRIMARY_LIST_ID = 'primaryBusinesses'
    const SECONDARY_LIST_ID = 'secondaryBusinesses'

    return <>
    {/* PRIMARY */}
    {
      primaryList && primaryList.companies && primaryList.companies.length > 0 ? (
        <View>
          <Text
            style={[
              textStyles.h3,
              {
                marginBottom: 5,
                paddingHorizontal: 16,
                color: textMain
              }
            ]}
          >
            {primaryList?.label || "Featured Businesses"}
          </Text>
          <CompanyViewFlatlist
            id="primaryBusinesses"
            data={primaryList?.companies}
            renderItem={({ item, index }) => {
              return renderFeaturedBusiness({ item, index, source: PRIMARY_LIST_ID })
            }}
            keyExtractor={item => item._id}
            source={PRIMARY_LIST_ID}
            initialNumToRender={primaryList?.companies?.length}
          />
        </View>
      ) : null
    }
    {/* SECONDARY */}
    {
      secondaryList && secondaryList.companies && secondaryList.companies.length > 0 ? (
        <View>
          <Text
            style={[
              textStyles.h3,
              {
                paddingHorizontal: 16,
                marginBottom: 5,
                color: textMain
              }
            ]}
          >
            {secondaryList?.label || "More Featured Businesses"}
          </Text>
          <CompanyViewFlatlist
            id="secondaryBusinesses"
            data={secondaryList?.companies}
            renderItem={({ item, index }) => {
              return renderFeaturedBusiness({ item, index, source: SECONDARY_LIST_ID })
            }}
            keyExtractor={item => item._id}
            source={SECONDARY_LIST_ID}
            initialNumToRender={secondaryList?.companies?.length}
          />
        </View>
      ) : null
    }
    </>
  }

  return (
    <>
    <View style={{alignSelf: "center", width: "100%", maxWidth: MAX_CONTENT_WIDTH, gap: 10, paddingHorizontal: 16}}>
      <Text
        style={[textStyles.h2, {color: textMain}]}
      >
        Find a Values-Based Business
      </Text>
      {renderFeaturedBusinessContent()}
      <View style={{paddingVertical: 12, alignSelf: "flex-start", marginLeft: 16}}>
        <Link
          onPress={() => Haptics.selection()}
          action={CommonActions.navigate({
            name: "ExploreCompaniesStack",
            params: {
              screen: "ExploreCompaniesHome",
              pop: true
            }
          })}
          href={"/explore-companies"}
        >
          <Text style={[textStyles.h4, { color: primary }]}>
            Explore More Companies
          </Text>
        </Link>
      </View>
    </View>
    {/* error modal */}
    <ThemedDialog
      visible={errorState}
      errorAnimation={errorState}
      title="Error"
      subMessage="An error occurred while retrieving company information. Please try again later."
      options={[
        {
          title: "Okay",
          role: "cancel",
          icon: <Ionicons name="checkmark" size={22} />,
          onPress: () => setErrorState(false),
        },
      ]}
    />
    </>
  )
}

export default DashboardBusinesses

const styles = StyleSheet.create({
  flatListContainer: {
    gap: 10,
    paddingHorizontal: 5,
    paddingVertical: 5,
  },
  card: {
    borderRadius: 10,
    overflow: "hidden",
    flexDirection: "column",
    paddingVertical: 16
  },
  featuredCard: {
    width: 250,
    borderRadius: 8,
  },
  errorCard: {
    marginHorizontal: 16,
    padding: 16,
    borderRadius: 10,
    flexDirection: "row",
  },
  featuredTextContainer: {
    width: "100%",
    paddingTop: 12,
    paddingBottom: 20,
    paddingHorizontal: 16,
    flexDirection: "column",
    gap: 5
  },
  valueBadgesRow: {
    height: 24,
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
})